import {
  ALIGN_CENTER,
  ALIGN_LEFT,
  BOLD_OFF,
  BOLD_ON,
  CUT,
  FEED_BEFORE_CUT,
  INIT,
  SIZE_NORMAL,
  SIZE_TALL,
  WIDTH,
  feed,
  leftRight,
  line,
  rule,
  truncate,
} from './escpos';
import { printRaw } from './qzPrinting';
import { paperDate, paperTime } from './paperDate';

/**
 * The customer's receipt.
 *
 * Lifted out of the print dialog unchanged — same columns, same wording, same
 * order of lines — so that it can be printed from two places: the dialog, and
 * automatically when the business is set to auto-print. One builder means the
 * automatic copy and the one behind the button can never drift apart.
 */

/**
 * The Windows name of the receipt printer on every till.
 *
 * Fixed on purpose: machines are set up with the printer renamed to this, so
 * the app never has to ask. A till that says no printer was found has a
 * printer under a different name in Windows.
 */
export const RECEIPT_PRINTER = 'Local Printer';

/** Column widths, adding up to the 48 characters of 80mm paper. */
const QTY_WIDTH = 5;
const ITEM_WIDTH = 22;
const PRICE_WIDTH = 10;
const TOTAL_WIDTH = 11;

/**
 * Everything the receipt shows, worked out once.
 *
 * The dialog draws its preview from these and prints from the same object, so
 * what a cashier sees on screen is what comes out of the printer.
 */
export function receiptFields({ business, data }) {
  const { discount, date, products } = data || {};

  const subtotal =
    (products || []).reduce(
      (sum, product) =>
        sum + Number(product.salePrice || 0) * Number(product.qty || 0),
      0
    ) || 0;

  const discountValue =
    typeof discount === 'number' && !Number.isNaN(discount) ? parseFloat(discount) : 0;

  /**
   * VAT, as the server charged it.
   *
   * Nothing is computed here on purpose. The rate is snapshotted on the order
   * and the amount is taken after the discount, so a receipt reprinted next
   * year still shows the tax that was actually charged — a client that
   * re-derived it from today's business rate would print a different number
   * from the one in the books.
   *
   * An order with no VAT has `vatAmount` 0, and the receipt then looks exactly
   * as it always did: one total line, no tax lines.
   */
  const vatAmount = Number(data?.vatAmount) || 0;
  const vatRate = Number(data?.vatRate) || 0;
  const total = subtotal - discountValue;

  return {
    products: products || [],
    subtotal,
    discount: discountValue,
    total,

    vatRate,
    vatAmount,
    // What the customer pays. The server's own figure when it sent one, so the
    // paper and the database cannot disagree by a rounded cent.
    grandTotal:
      data?.totalWithVat === undefined || data?.totalWithVat === null
        ? total + vatAmount
        : Number(data.totalWithVat),

    formattedDate: paperDate(date),
    formattedTime: paperTime(date),

    orderNumber: data?.orderNumber || data?.orderNo || data?.id || data?._id || '',

    /**
     * Who served this table.
     *
     * The cell here used to say TIXRAAC and was never filled — no caller ever
     * passed a reference. The phone prints the waiter in that spot, and that
     * is the line a customer actually asks about, so the till now prints the
     * same thing in the same place.
     *
     * On a reprint from the Orders screen the name comes off the saved order,
     * so an old receipt still names whoever took it rather than whoever is
     * standing at the till today.
     */
    waiterName: String(data?.waiterName || data?.user?.name || '').trim(),

    businessName: business?.businessName || 'DALABTIRE APP',
    businessLocation:
      business?.location || business?.city || business?.businessLocation || 'Somalia',
    businessNumber: business?.businessNumber || '',
    paytoAccounts: business?.paytoAccounts || [],
  };
}

/** One product line: `2x   Bariis Iskukaris        $7.00      $14.00`. */
function productRow(product) {
  const qty = `${product.qty}x`.padEnd(QTY_WIDTH);
  const name = truncate(product.name, ITEM_WIDTH - 1).padEnd(ITEM_WIDTH);
  const price = `$${Number(product.salePrice || 0).toFixed(2)}`.padStart(PRICE_WIDTH);
  const total = `$${(
    Number(product.salePrice || 0) * Number(product.qty || 0)
  ).toFixed(2)}`.padStart(TOTAL_WIDTH);

  return qty + name + price + total + '\n';
}

export function buildReceipt(fields) {
  let out = INIT;

  // The paper the printer has already pulled past the head when it cut the
  // last receipt — without it the shop name starts halfway down.
  out += feed(4);

  /* ── who ── */

  out += ALIGN_CENTER;
  out += SIZE_TALL + BOLD_ON;
  out += line(fields.businessName);
  out += BOLD_OFF + SIZE_NORMAL;

  out += line(fields.businessLocation);
  if (fields.businessNumber) out += line(fields.businessNumber);

  out += feed(1);
  fields.paytoAccounts.forEach((payment) => {
    out += line(`${payment.label}: ${payment.account}`);
  });
  out += feed(1);

  /* ── which order ── */

  out += ALIGN_LEFT;
  out += leftRight(`ORDER: #${fields.orderNumber}`, fields.formattedDate);

  // Trimmed to what is left of the line once the label and the time have
  // taken theirs — a long name would otherwise push the time onto a line of
  // its own and leave the receipt looking broken.
  const waiterLabel = 'WAITER: ';
  const waiterRoom = WIDTH - waiterLabel.length - fields.formattedTime.length - 1;
  out += leftRight(
    waiterLabel + truncate(fields.waiterName, waiterRoom),
    fields.formattedTime
  );

  out += rule('-');

  /* ── what was bought ── */

  out += BOLD_ON;
  out +=
    'Qty'.padEnd(QTY_WIDTH) +
    'Item'.padEnd(ITEM_WIDTH) +
    'Price'.padStart(PRICE_WIDTH) +
    'Total'.padStart(TOTAL_WIDTH) +
    '\n';
  out += BOLD_OFF;

  fields.products.forEach((product) => {
    out += productRow(product);
  });

  out += rule('-');

  /* ── what it came to ── */

  // With no VAT this is one bold line, exactly as before. With VAT the
  // customer gets the three lines a tax receipt has to show.
  if (fields.vatAmount > 0) {
    out += leftRight('Total', `$${fields.total.toFixed(2)}`);
    out += leftRight(
      fields.vatRate ? `VAT (${fields.vatRate}%)` : 'VAT',
      `$${fields.vatAmount.toFixed(2)}`
    );
  }

  out += BOLD_ON;
  out += leftRight('Wadarta Guud', `$${fields.grandTotal.toFixed(2)}`);
  out += BOLD_OFF;

  /* ── goodbye ── */

  out += feed(1);
  out += ALIGN_CENTER;
  out += line('Mahadsanid, Soo noqosho wacan!');
  out += line('Powered By Dalabtire!');

  out += feed(FEED_BEFORE_CUT);
  out += CUT;

  return out;
}

/** Build it and send it to the till's own printer. */
export function printReceipt({ business, data }) {
  return printRaw({ name: RECEIPT_PRINTER }, buildReceipt(receiptFields({ business, data })));
}

export { WIDTH };
